// src/app/components/analytics/series/series-chart-panel.tsx
//
// Renders ONE lens as a chart through the existing shared shell (AnalyticsChart),
// with identical formatting to the current Performance charts. The lens is shown
// at its default breakdown (per-model where available), matching how the current
// per-model charts render. No extra chrome, so a stack of these reads exactly
// like the current stacked charts.

import { useMemo } from "react";
import { AnalyticsChart } from "../analytics-chart";
import { MetricTerm } from "../../metric-term";
import { buildLensChartData, incompleteRunMarks } from "@/app/services/performance-series/selectors";
import { defaultDimension, type Lens } from "@/app/services/performance-series/registry";
import { getMetricDefinition } from "@/app/services/performance-series/metric-definitions";
import type { NormalizedEnvelope } from "@/app/services/performance-series/types";
import type { PerformanceEvent } from "@/app/data/performance-events";

interface Props {
  envelope: NormalizedEnvelope;
  lens: Lens;
  brandName?: string;
  projectionDate?: string | null;
  events?: PerformanceEvent[];
  onNavigateToInsights?: () => void;
}

export function SeriesChartPanel({
  envelope,
  lens,
  brandName,
  projectionDate,
  events,
  onNavigateToInsights,
}: Props) {
  const dimension = defaultDimension(lens.dimensions);
  const chart = useMemo(
    () => buildLensChartData(envelope, lens.layer, dimension),
    [envelope, lens.layer, dimension],
  );
  const incompleteRuns = useMemo(
    () => incompleteRunMarks(envelope, lens.layer),
    [envelope, lens.layer],
  );

  // Restore the click-to-define modal on the metric title where a definition
  // exists (parity with the legacy per-metric charts).
  const def = getMetricDefinition(lens.key);
  const title = def
    ? <MetricTerm definition={def.definition} calculation={def.calculation} interpretation={def.interpretation} goodRange={def.goodRange}>{lens.label}</MetricTerm>
    : lens.label;

  return (
    <AnalyticsChart
      timeSeriesData={chart.timeSeriesData}
      selectedModels={chart.seriesKeys}
      isLoading={false}
      error={null}
      metricUnit={chart.metricUnit}
      metricName={lens.label}
      metricTitle={title}
      brandName={brandName}
      onNavigateToInsights={onNavigateToInsights}
      projectionDate={projectionDate}
      events={events}
      incompleteRuns={incompleteRuns}
    />
  );
}
